<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useKeyboardClose } from './useKeyboardClose';

const props = withDefaults(
  defineProps<{
    header?: string;

    noHeader?: boolean;
    noFooter?: boolean;
    emptyFooter?: boolean;
    noHeaderTitle?: boolean;
    noHeaderClose?: boolean;

    compact?: boolean;
    noCloseButton?: boolean;
    noPrimaryButton?: boolean;

    noBackdrop?: boolean;
    noShow?: boolean;
    loading?: boolean;

    closeButtonText?: string;
    primaryButtonText?: string;
    submitButtonText?: string;

    headerClass?: string;
    contentClass?: string;
    footerClass?: string;

    size?: 'sm' | 'md' | 'lg' | 'xl' | 'full-screen';

    form?: any | null;

    invalid?: boolean;

    dirty?: boolean;

    confirmOnClose?: boolean;
    escToClose?: boolean;
    backdropToClose?: boolean;

    global?: boolean;
  }>(),
  {
    noHeader: false,
    noFooter: false,
    emptyFooter: false,
    noHeaderTitle: false,
    noHeaderClose: false,

    compact: false,
    noCloseButton: false,
    noPrimaryButton: false,

    noBackdrop: false,
    noShow: false,
    loading: false,

    closeButtonText: 'Close',
    primaryButtonText: 'Ok',
    submitButtonText: 'Submit',

    size: undefined,
    form: null,

    invalid: false,
    dirty: false,
    confirmOnClose: false,
    escToClose: true,
    backdropToClose: false,

    global: false,
  },
);

const emit = defineEmits<{
  (event: 'close', result?: any): void;
  (event: 'submit'): void;
  (event: 'commit'): void;
}>();

const modalDialogClass = computed(() => ({
  compact: !!props.compact,
  'modal-xl': props.size === 'xl',
  'modal-lg': props.size === 'lg',
  'modal-sm': props.size === 'sm',
  'modal-full-screen': props.size === 'full-screen',
}));

const route = useRoute();
const ownerPath = ref(route.fullPath);
const isVisible = computed(() => !props.noShow);
const isRouteActive = computed(
  () => props.global || route.fullPath === ownerPath.value,
);

const showFooter = computed(
  () => !props.noFooter && !props.compact && !props.loading,
);
const showPrimaryButton = computed(
  () =>
    !props.form &&
    !props.noPrimaryButton &&
    !props.emptyFooter &&
    !props.compact &&
    !props.loading,
);
const showSubmitButton = computed(
  () =>
    !!props.form &&
    !props.noPrimaryButton &&
    !props.emptyFooter &&
    !props.compact &&
    !props.loading,
);
const showCloseButton = computed(
  () =>
    !props.emptyFooter &&
    !props.noCloseButton &&
    !props.compact &&
    !props.loading,
);
const showHeaderClose = computed(() => !props.noHeaderClose && !props.loading);

function canClose(): boolean {
  if (props.form && props.confirmOnClose && props.dirty) {
    return window.confirm(
      'You have unsaved changes. Do you want to close this dialog?',
    );
  }
  return true;
}

function beginClose(_event: Event, result?: any) {
  if (!canClose()) {
    return;
  }
  emit('close', result);
}

function beginSubmit(_event: Event) {
  emit('submit');
}

function beginCommit(_event: Event) {
  emit('commit');
}

const containerRef = ref<HTMLElement | null>(null);
useKeyboardClose({
  container: () => containerRef.value,
  onEscape: () => {
    if (!props.escToClose || !isRouteActive.value) {
      return;
    }
    beginClose(new Event('escape'), { cancel: true });
  },
});
</script>

<template>
  <div v-if="isVisible" v-show="isRouteActive" class="modal-in-portal">
    <div
      ref="containerRef"
      class="modal show d-block"
      tabindex="-1"
      role="dialog"
      data-bs-backdrop="static"
      aria-modal="true"
    >
      <div
        class="modal-dialog modal-dialog-centered modal-dialog-scrollable"
        :class="modalDialogClass"
      >
        <component
          :is="form ? 'form' : 'div'"
          class="modal-content"
          @submit.stop.prevent="beginSubmit"
        >
          <slot v-if="!noHeader" name="header">
            <div
              v-if="showHeaderClose"
              class="modal-header"
              :class="headerClass"
            >
              <slot v-if="!noHeaderTitle" name="headerTitle">
                <h5 class="modal-title">{{ header }}</h5>
              </slot>

              <slot name="headerCenter" />

              <slot v-if="showHeaderClose" name="headerClose">
                <button
                  type="button"
                  class="close"
                  aria-label="Close"
                  @click="beginClose($event, { cancel: true })"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </slot>
            </div>
          </slot>
          <div
            class="modal-body"
            :class="[
              contentClass,
              {
                'd-flex flex-column align-items-center justify-content-center':
                  loading,
              },
            ]"
            :style="loading ? 'min-height: 50vh;' : ''"
          >
            <template v-if="loading">
              <div
                class="spinner-border text-primary mb-3"
                role="status"
                style="width: 3rem; height: 3rem"
              >
                <span class="visually-hidden">Loading...</span>
              </div>
              <h5 class="text-muted">Loading...</h5>
            </template>

            <div v-else class="w-100 h-100">
              <slot />
            </div>
          </div>
          <slot v-if="showFooter" name="footer">
            <div class="modal-footer" :class="footerClass">
              <slot name="footer">
                <button
                  v-if="showPrimaryButton"
                  type="button"
                  class="btn btn-primary"
                  @click="beginCommit"
                >
                  {{ primaryButtonText }}
                </button>
                <button
                  v-if="showSubmitButton"
                  type="submit"
                  class="btn btn-primary"
                  :disabled="invalid"
                >
                  {{ submitButtonText }}
                </button>
                <button
                  v-if="showCloseButton"
                  type="button"
                  class="btn btn-secondary"
                  @click="beginClose($event, { cancel: true })"
                >
                  {{ closeButtonText }}
                </button>
              </slot>
            </div>
          </slot>
        </component>
      </div>
    </div>
    <div
      v-if="!noBackdrop"
      class="modal-backdrop show"
      @click="backdropToClose && beginClose($event, { cancel: true })"
    />
  </div>
</template>
